import { useEffect, useRef, useState, type MutableRefObject } from "react";
import { useGetIdentity, useNotify } from "ra-core";
import { Flag, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

import type { EchoflicksVideoSession } from "../types";
import { FeedbackCategoryFieldset } from "../feedback/FeedbackCategoryFieldset";
import { feedbackApiRequest } from "../feedback/echoFeedbackClient";
import {
  resolveFeedbackMetadata,
  type FeedbackMetadataInput,
} from "../feedback/feedbackMetadata";
import {
  ECHO_FEEDBACK_METADATA_SCHEMA_VERSION,
  ECHO_FEEDBACK_SCHEMA_VERSION,
} from "../feedback/feedbackContracts";
import {
  canonicalizeFeedbackCategoryIds,
  type FeedbackCategoryId,
} from "../feedback/feedbackTaxonomy";
import { formatVideoTimestamp as formatTimestamp } from "./sessionUtils";
import { captureVideoFrame } from "./videoFrameCapture";

type FrameCapture = {
  id: string;
  timestampSeconds: number;
  videoDurationSeconds: number | null;
  status: "capturing" | "ready" | "unavailable";
  frameDataUrl: string | null;
};

type FeedbackSubmissionResponse = {
  ok?: boolean;
  persisted?: boolean;
  notification?: { status?: string };
  data?: { notification?: { status?: string } };
};

export type EchoFeedbackContext = Omit<
  FeedbackMetadataInput,
  "record" | "position" | "categoryIds" | "capturedAt"
>;

/**
 * Timestamp-aware internal QC report action. The browser captures a bounded
 * screenshot and an allowlisted diagnostic snapshot; the server derives the
 * reporter/environment, persists first, and sends only a concise Slack view.
 */
export const EchoFeedbackButton = ({
  record,
  videoRef,
  videoUrl,
  context,
}: {
  record: EchoflicksVideoSession;
  videoRef: MutableRefObject<HTMLVideoElement | null>;
  videoUrl: string;
  context?: EchoFeedbackContext;
}) => {
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const captureController = useRef<AbortController | null>(null);
  const resumeAfterClose = useRef(false);
  const [open, setOpen] = useState(false);
  const [capture, setCapture] = useState<FrameCapture | null>(null);
  const [feedback, setFeedback] = useState("");
  const [categoryIds, setCategoryIds] = useState<FeedbackCategoryId[]>([]);
  const [clientRequestId, setClientRequestId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const echoName =
    record.movie_title ?? record.script_title ?? record.session_id ?? "Echo";

  useEffect(
    () => () => {
      captureController.current?.abort();
    },
    [],
  );

  if (identity?.administrator !== true) return null;

  const restorePlayback = () => {
    if (!resumeAfterClose.current) return;
    resumeAfterClose.current = false;
    void videoRef.current?.play().catch(() => undefined);
  };

  const handleOpen = () => {
    captureController.current?.abort();
    const controller = new AbortController();
    captureController.current = controller;
    const video = videoRef.current;
    const duration = finiteOrNull(video?.duration);
    const current = finiteOrNull(video?.currentTime) ?? 0;
    const timestampSeconds =
      duration == null ? Math.max(0, current) : clamp(current, 0, duration);
    const captureId = crypto.randomUUID();

    resumeAfterClose.current = Boolean(video && !video.paused);
    video?.pause();
    setFeedback("");
    setCategoryIds([]);
    setClientRequestId(crypto.randomUUID());
    setCapture({
      id: captureId,
      timestampSeconds,
      videoDurationSeconds: duration,
      status: "capturing",
      frameDataUrl: null,
    });
    setOpen(true);
    captureVideoFrame(videoUrl, timestampSeconds, controller.signal).then(
      (frameDataUrl) => {
        if (controller.signal.aborted) return;
        setCapture((currentCapture) =>
          currentCapture?.id === captureId
            ? {
                ...currentCapture,
                status: frameDataUrl ? "ready" : "unavailable",
                frameDataUrl,
              }
            : currentCapture,
        );
      },
    );
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) return;
    captureController.current?.abort();
    captureController.current = null;
    restorePlayback();
  };

  const toggleCategory = (categoryId: FeedbackCategoryId, checked: boolean) => {
    setCategoryIds((current) =>
      canonicalizeFeedbackCategoryIds(
        checked
          ? [...current, categoryId]
          : current.filter((id) => id !== categoryId),
      ),
    );
  };

  const handleSubmit = async () => {
    if (!capture || !clientRequestId || !feedback.trim()) return;
    if (capture.status === "capturing") {
      captureController.current?.abort();
      captureController.current = null;
      setCapture((currentCapture) =>
        currentCapture?.id === capture.id
          ? {
              ...currentCapture,
              status: "unavailable",
              frameDataUrl: null,
            }
          : currentCapture,
      );
    }
    const video = videoRef.current;
    const canonicalCategoryIds = canonicalizeFeedbackCategoryIds(categoryIds);
    const metadata = resolveFeedbackMetadata({
      ...(context ?? {}),
      record,
      categoryIds: canonicalCategoryIds,
      position: {
        timestampMs: Math.round(capture.timestampSeconds * 1000),
        videoDurationMs:
          capture.videoDurationSeconds == null
            ? null
            : Math.round(capture.videoDurationSeconds * 1000),
        videoWidth: finiteOrNull(video?.videoWidth),
        videoHeight: finiteOrNull(video?.videoHeight),
        playbackRate: finiteOrNull(video?.playbackRate),
      },
    });

    setSubmitting(true);
    try {
      const response = await feedbackApiRequest<FeedbackSubmissionResponse>(
        "/api/echo-feedback",
        {
          method: "POST",
          body: JSON.stringify({
            schemaVersion: ECHO_FEEDBACK_SCHEMA_VERSION,
            metadataSchemaVersion: ECHO_FEEDBACK_METADATA_SCHEMA_VERSION,
            clientRequestId,
            echoId: String(record.id),
            timestampMs: Math.round(capture.timestampSeconds * 1000),
            videoDurationMs:
              capture.videoDurationSeconds == null
                ? null
                : Math.round(capture.videoDurationSeconds * 1000),
            comment: feedback.trim(),
            categoryIds: canonicalCategoryIds,
            frameDataUrl:
              capture.status === "ready" ? capture.frameDataUrl : null,
            metadata,
          }),
        },
      );
      const notificationStatus =
        response.notification?.status ?? response.data?.notification?.status;
      if (notificationStatus && notificationStatus !== "sent") {
        notify(
          "Feedback saved. The Slack alert is pending and can be retried from Feedback.",
          {
            type: "warning",
          },
        );
      } else {
        notify("Feedback saved and sent to #echo-quality-control", {
          type: "success",
        });
      }
      handleOpenChange(false);
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "Could not save the feedback",
        { type: "error" },
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button variant="destructive" size="sm" onClick={handleOpen}>
        <Flag className="size-4" />
        Feedback here
      </Button>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Feedback at {formatTimestamp(capture?.timestampSeconds ?? 0)}
            </DialogTitle>
            <DialogDescription>
              Select any issue types that apply and describe what is wrong in
              this moment of “{echoName}”. Your comment, Echo identity,
              timestamp, screenshot, and diagnostic context are saved privately
              in the CRM. Slack receives only category labels and a generic link
              to the authenticated Feedback tab.
            </DialogDescription>
          </DialogHeader>

          <div className="overflow-hidden rounded-md border bg-black">
            {capture?.status === "ready" && capture.frameDataUrl ? (
              <img
                src={capture.frameDataUrl}
                alt={`Video frame at ${formatTimestamp(capture.timestampSeconds)}`}
                className="max-h-64 w-full object-contain"
              />
            ) : (
              <div className="flex aspect-video items-center justify-center px-4 text-center text-sm text-white/70">
                {capture?.status === "capturing" ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="size-4 animate-spin" />
                    Capturing the frame...
                  </span>
                ) : (
                  "Screenshot unavailable — the feedback will still include its timestamp and metadata."
                )}
              </div>
            )}
          </div>

          <FeedbackCategoryFieldset
            selected={categoryIds}
            onChange={toggleCategory}
          />

          <div className="space-y-2">
            <label
              htmlFor="echo-feedback-details"
              className="text-sm font-medium"
            >
              What is wrong?
            </label>
            <Textarea
              id="echo-feedback-details"
              value={feedback}
              onChange={(event) => setFeedback(event.target.value)}
              placeholder="Describe the issue and what a better edit would do."
              rows={4}
              maxLength={4000}
              autoFocus
            />
            <div className="text-right text-xs text-muted-foreground">
              {feedback.length}/4000
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleSubmit}
              disabled={submitting || !capture || !feedback.trim()}
            >
              {submitting ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  Saving...
                </>
              ) : capture?.status === "capturing" ? (
                "Save without screenshot"
              ) : (
                "Save feedback"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

function finiteOrNull(value: number | null | undefined) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}
